import {
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  deleteBuyOrderStore,
  saveEditOwnerShares,
} from '../buy-order-edit/buy-order-edit-redux';
import { CUSTOMER_FORM_ID } from '../../../../global/constants';
import { BuyOrderApiResponse } from '../buy-order-detailed/buy-order-detailed-types';

export type BuyOrderOrganizationSigner = {
  socialSecurityNumber: string;
  signerId?: string;
};

export type BuyOrderOrganizationEntity = {
  organizationAddress: string;
  organizationCity: string;
  organizationId?: string;
  organizationName: string;
  organizationNumber: string;
  organizationPostalCode: string;
  // A legal-person shareholder can be represented by up to four authorized representatives.
  signers: BuyOrderOrganizationSigner[];
};

export const buyOrderOrganizationsAdapter = createEntityAdapter<BuyOrderOrganizationEntity>({
  selectId: (x) => x.organizationNumber,
});

const buyOrderOrganizationsSlice = createSlice({
  name: 'buyOrderOrganizations',
  initialState: {
    [CUSTOMER_FORM_ID]: {
      entities: {},
      ids: [],
    },
  } as Record<string, EntityState<BuyOrderOrganizationEntity>>,
  reducers: {
    initBuyOrderOrganizations(state, action: PayloadAction<{ id: string }>) {
      if (!state[action.payload.id]) {
        state[action.payload.id] = {
          entities: {},
          ids: [],
        };
      }
    },
    saveBuyOrderOrganizations(
      state,
      action: PayloadAction<{
        id: string;
        buyOrder?: BuyOrderApiResponse;
      }>,
    ) {
      const { id, buyOrder } = action.payload;

      if (buyOrder) {
        buyOrderOrganizationsAdapter.upsertMany(
          state[id],
          buyOrder.organizations.map((organization) => ({
            organizationAddress: organization.address,
            organizationName: organization.name,
            organizationPostalCode: organization.postalCode,
            organizationCity: organization.city,
            organizationNumber: organization.number,
            organizationId: organization.id,
            signers: organization.signers.map((signer) => ({
              socialSecurityNumber: signer.person.socialSecurityNumber,
              signerId: signer.id,
            })),
          })),
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveEditOwnerShares, (state, action) => {
      const { legalShareholders, id } = action.payload;

      if (legalShareholders) {
        buyOrderOrganizationsAdapter.upsertMany(
          state[id],
          legalShareholders.map((legal) => ({
            organizationAddress: legal.organizationAddress,
            organizationCity: legal.organizationCity,
            organizationName: legal.organizationName,
            organizationNumber: legal.organizationNumber,
            organizationPostalCode: legal.organizationPostalCode,
            organizationId: legal.organizationId || undefined,
            signers: (legal.representatives ?? [])
              .filter((representative) => representative.socialSecurityNumber)
              .map((representative) => ({
                socialSecurityNumber: representative.socialSecurityNumber,
                signerId: representative.signerId || undefined,
              })),
          })),
        );
      }
    });

    builder.addCase(deleteBuyOrderStore, (state, action) => {
      state[action.payload.id] = {
        entities: {},
        ids: [],
      };
    });
  },
});

export const buyOrderOrganizationsSelectors = buyOrderOrganizationsAdapter.getSelectors();
export const buyOrderOrganizationsReducer = buyOrderOrganizationsSlice.reducer;
export const { initBuyOrderOrganizations, saveBuyOrderOrganizations } = buyOrderOrganizationsSlice.actions;
